<template>
  <div v-if="!isNoItems" ref="sliderRef" :class="$style.catalogueSliderProvidersContainer">
    <CatalogueSliderHeader
      :icon="providersIcon.url"
      :name="providersTitle"
      :is-loading="!flickingRef"
      :show-all-title="showAllTitle"
      :show-all-link="storeRoot.localePath('/providers')"
      @showAll="goToAllProviders"
    />
    <template v-if="!isProvidersLoading && providers.length && !isNoShow">
      <ClientOnly>
        <Flicking
          ref="flickingRef"
          hide-before-init
          :options="{
            bound: true,
            align: 'prev',
            panelsPerView: slidesPerView,
            inputType: isDesktop ? [] : ['touch', 'mouse'],
          }"
          @ready="sliderOnReady"
        >
          <div
            v-for="item in providersByTwo"
            :key="item[0] && item[0].providerId"
            :class="$style.catalogueSliderProvidersItemDouble"
          >
            <div v-if="item[0]" :class="$style.catalogueSliderProvidersItemSingle">
              <a
                :href="providerLink(item[0].providerId)"
                :class="$style.catalogueSliderProvidersItemLink"
                :aria-label="`Got to provider ${item[0].providerTitle}`"
                @click.prevent="openProvider(item[0].providerId)"
              >
                <img
                  :src="
                    (item[0].providerIconWide && item[0].providerIconWide.url) ||
                    item[0].providerIcon?.url
                  "
                  alt="selectorsSearchResultsItem"
                />
              </a>
            </div>
            <div v-if="item[1]" :class="$style.catalogueSliderProvidersItemSingle">
              <a
                :href="providerLink(item[1].providerId)"
                :class="$style.catalogueSliderProvidersItemLink"
                :aria-label="`Got to provider ${item[1].providerTitle}`"
                @click.prevent="openProvider(item[1].providerId)"
              >
                <img
                  :src="
                    (item[1].providerIconWide && item[1].providerIconWide.url) ||
                    item[1].providerIcon?.url
                  "
                  alt="selectorsSearchResultsItem"
                />
              </a>
            </div>
          </div>
        </Flicking>
      </ClientOnly>
    </template>
    <CatalogueSliderProvidersSkeleton v-else :max-slides-per-view="maxSlidesPerView" />
  </div>
</template>

<script lang="ts" setup>
import { Arrow } from '@egjs/flicking-plugins';
import Flicking, { type FlickingEvents } from '@egjs/vue3-flicking';
import { computed, nextTick, ref, watch } from 'vue';
import CatalogueSliderHeader from '~/components/mainPage/catalogueSliderHeader.vue';
import CatalogueSliderProvidersSkeleton from '~/components/mainPage/catalogueSliderProvidersSkeleton.vue';
import { useStoreRoot } from '~/stores/abstract/root';
import '@egjs/vue3-flicking/dist/flicking.css';
import { useStoreSsGames } from '~/stores/softswiss/games';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreCataloguePage } from '~/stores/strapi/cataloguePage';
import { useStoreProviders } from '~/stores/strapi/providers';
import type { FlickingType } from '~/types/banners';
import type { IProviderData } from '~/types/providers';

const props = defineProps({
  showAllTitle: {
    type: String,
    default: '',
  },
  isNoShow: {
    type: Boolean,
    default: true,
  },
  maxSlidesPerView: {
    type: Number,
    default: undefined,
  },
});
defineEmits<{
  (e: 'loaded'): void;
}>();

const storeRoot = useStoreRoot();
const storeProviders = useStoreProviders();
const storeSsPlayer = useStoreSsPlayer();
const storeSsGames = useStoreSsGames();
const storeCataloguePage = useStoreCataloguePage();

const sliderRef = ref<null | HTMLElement>(null);
const providersByCurrency = ref<string[]>([]);
const isProvidersLoading = ref<boolean>(true);
const flickingRef = ref<null | FlickingType>(null);

const slidesPerView = computed(() => {
  let count = 3;
  if (storeRoot.screen.width > 2560) {
    count = 12;
  } else if (storeRoot.screen.width <= 2560 && storeRoot.screen.width > 1632) {
    count = 8;
  } else if (storeRoot.screen.width <= 1632 && storeRoot.screen.width > 768) {
    count = 6;
  } else if (storeRoot.screen.width <= 768 && storeRoot.screen.width > 580) {
    count = 4;
  } else {
    count = 3;
  }
  return props.maxSlidesPerView ? Math.min(count, props.maxSlidesPerView) : count;
});

const providersTitle = computed(() => storeCataloguePage.allProvidersTitle);
const providersIcon = computed(() => storeCataloguePage.allProvidersIcon);

/**
 * Method that takes first letter from providers,
 * or fallback for numerics (for example 1x2 gaming)
 */
const getAlphabeticKey = (providerId: string): string => {
  const numericKey = '0-9';
  const firstProviderCharac = providerId[0];

  // parseInt returns NaN or number
  const currObjKey = isNaN(parseInt(firstProviderCharac))
    ? firstProviderCharac.toUpperCase()
    : numericKey;

  return currObjKey;
};
const isDesktop = computed(() => usePlatform().isDesktop);

const sortedProvidersWithLetters = computed(() => {
  const usedLetters: string[] = [];
  return providers.value.map((provider) => {
    const firstLetter = getAlphabeticKey(provider.providerId);
    const shouldShowLetter = !usedLetters.includes(firstLetter);

    if (shouldShowLetter) {
      usedLetters.push(firstLetter);
    }

    return provider;
  });
});

const providers = computed(() => {
  return storeProviders.activeList.filter(
    (p) =>
      !!providersByCurrency.value?.find((provider) => new RegExp(p.providerId, 'i').test(provider))
  );
});

const isNoItems = computed(() => {
  if (isProvidersLoading.value) {
    return false;
  }
  return !providers.value?.length;
});

const providersByTwo = computed(() => {
  return sortedProvidersWithLetters.value.reduce<Array<IProviderData[]>>(
    (acc, el) => {
      if (acc[acc.length - 1].length < 2) {
        acc[acc.length - 1].push(el);
      } else {
        acc.push([el]);
      }

      return acc;
    },
    [[]]
  );
});

const sliderOnReady = (event: FlickingEvents['ready']) => {
  const currentTarget = event.currentTarget;
  currentTarget?.addPlugins(
    new Arrow({
      parentEl: sliderRef.value,
    })
  );
};

const loadProviders = async () => {
  providersByCurrency.value = await storeSsGames.loadProviders();
};

const providerLink = (provider?: string) => {
  return storeRoot.localePath(`/${provider || ''}`);
};

const openProvider = async (provider?: string) => {
  if (import.meta.browser && sessionStorage && sessionStorage.gamesCount) {
    delete sessionStorage.gamesCount;
  }
  await navigateTo({ path: providerLink(provider) });
};

const goToAllProviders = () => {
  return navigateTo({ path: storeRoot.localePath('/providers') });
};

watch(
  () => [storeRoot.userCurrency, storeSsPlayer.isPlayerAuthorised],
  async () => await loadProviders()
);
watch(
  () => providersByCurrency.value,
  () => {
    isProvidersLoading.value = true;
    nextTick(() => (isProvidersLoading.value = false));
  }
);
watch(
  () => props.isNoShow,
  async (val, oldVal) => {
    if (!val && val !== oldVal) {
      isProvidersLoading.value = true;
      await loadProviders();
      isProvidersLoading.value = false;
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" module src="./catalogueSliderProviders.scss"></style>
